import { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  applyGroupPaymentRefundSources,
  calculateGroupRefundCeiling,
} from '~/components/Refund/RefundV2/Utils/RefundUtils';
import { EnrichedRefundMetadata, RefundPayload, RefundSourceAllocation } from '~/components/Refund/types';

import { INSURANCE_BREAKAGE } from '~/consts/refund';

import { GroupPaymentForOrder, RefundableSourcesResult, getRefundableSources } from '~/services/PaymentsService';

interface Params {
  orderId: string;
  groupPayment?: GroupPaymentForOrder;
  reason?: string;
  refundMetadata?: EnrichedRefundMetadata;
}

/**
 * Group payment state shared by the item refund modals: the payer sources, the most they can
 * refund, the per-source allocation, and how both apply to the final refund payload.
 */
export default function useGroupPaymentRefund({ orderId, groupPayment, reason, refundMetadata }: Params) {
  const { enqueueSnackbar } = useSnackbar();
  const [refundSourceAllocations, setRefundSourceAllocations] = useState<Array<RefundSourceAllocation> | null>(null);
  const [refundableSources, setRefundableSources] = useState<RefundableSourcesResult | undefined>(undefined);

  const isGroupPaymentOrder = !!groupPayment;
  // Insurance breakage pays nothing back, so it takes the non-group flow (Manual, no refund_sources),
  // the same as the customer portal. svc-payment then writes off the unpaid shares and cancels the schedule.
  const usesGroupPaymentAllocation = isGroupPaymentOrder && reason !== INSURANCE_BREAKAGE;

  const groupRefundCeiling = useMemo(
    () =>
      isGroupPaymentOrder && refundableSources?.sources
        ? calculateGroupRefundCeiling(refundMetadata, refundableSources.sources)
        : undefined,
    [isGroupPaymentOrder, refundMetadata, refundableSources?.sources],
  );

  // Returns the sources, or undefined when they could not be loaded and the refund should stop.
  const loadRefundableSources = useCallback(async (): Promise<RefundableSourcesResult | undefined> => {
    try {
      const response = await getRefundableSources(orderId);
      const sources = response?.result;
      if (sources) {
        setRefundableSources(sources);
        return sources;
      }
    } catch {
      // Same message as an empty response.
    }
    enqueueSnackbar('Could not load the payment sources for this order. Please try again.', {
      variant: 'error',
    });
    return undefined;
  }, [orderId, enqueueSnackbar]);

  const applyRefundSources = useCallback(
    (mappedRefund: RefundPayload): RefundPayload => {
      // Shared with non-group refunds, where allocations stay null.
      // Applying with [] would set amount to 0 (cash total of nothing).
      if (!refundSourceAllocations?.length) {
        return mappedRefund;
      }
      return applyGroupPaymentRefundSources(mappedRefund, refundSourceAllocations);
    },
    [refundSourceAllocations],
  );

  return {
    isGroupPaymentOrder,
    usesGroupPaymentAllocation,
    refundableSources,
    groupRefundCeiling,
    refundSourceAllocations,
    setRefundSourceAllocations,
    loadRefundableSources,
    applyRefundSources,
  };
}
